import React, { useState, useEffect } from 'react';
import { costService } from '../services/costService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faChartBar,
  faChartPie,
  faGlobe,
  faFilter,
  faCalendar,
  faArrowTrendUp,
  faArrowTrendDown,
  faCompass,
  faServer,
  faDatabase,
  faShieldAlt,
  faTags,
  faCreditCard,
  faGauge,
  faPercent,
  faMoneyBill
} from '@fortawesome/free-solid-svg-icons';
import { CostData, CostFilters } from '../types';
import { useAIAnalysis, AIInsightsPanel, AIInsightsButton } from './AIInsightsPanel';

interface VisualizationTabProps {
  data: CostData | null;
  loading?: boolean;
  filters?: CostFilters;
  onFiltersChange?: (filters: CostFilters) => void;
}

interface ChartCardProps {
  title: string;
  description: string;
  icon: any;
  color: string;
  children: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  icon,
  color,
  children,
  loading = false,
  fullWidth = false
}) => {
  if (loading) {
    return (
      <div className={`bg-white border border-gray-300 rounded p-3 animate-pulse font-mono ${fullWidth ? 'col-span-1 lg:col-span-2' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-48"></div>
          </div>
          <div className={`w-10 h-10 bg-gray-200 rounded`}></div>
        </div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-300 rounded p-3 hover:shadow-md transition-shadow font-mono ${fullWidth ? 'col-span-1 lg:col-span-2' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
        <div className={`w-10 h-10 ${color} rounded flex items-center justify-center`}>
          <FontAwesomeIcon icon={icon} className="text-white text-sm" />
        </div>
      </div>
      <div className="h-64">
        {children}
      </div>
    </div>
  );
};

// 12-Month Trend Chart (real data from backend)
interface MonthData {
  month: string;
  label: string;
  aws: number;
  gcp: number;
  github: number;
  total: number;
}

const TwelveMonthTrendChart: React.FC<{ data: CostData | null; dateRange?: string }> = ({ data, dateRange = '12m' }) => {
  const [trendData, setTrendData] = useState<MonthData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getTwelveMonthAnalysis()
      .then(res => {
        if (!cancelled) {
          setTrendData(res?.months || []);
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load trends');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-gray-500">
        Loading 12-month trends...
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-red-600">
        {error}
      </div>
    );
  }

  if (!trendData || trendData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-gray-500">
        No trend data available
      </div>
    );
  }

  // Slice based on dateRange (3m, 6m, 12m) — show last N months
  const monthsToShow = dateRange === '3m' ? 3 : dateRange === '6m' ? 6 : 12;
  const visibleData = trendData.slice(-monthsToShow);
  const maxValue = Math.max(...visibleData.map(d => d.total), 1);

  const formatCost = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    if (n >= 1) return `$${n.toFixed(0)}`;
    return n > 0 ? '<$1' : '$0';
  };

  const titleLabel = `Past ${monthsToShow} Months Cost Trends`;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-gray-700">{titleLabel}</span>
        <div className="flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-orange-500 rounded"></div>
            <span>AWS</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span>GCP</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gray-600 rounded"></div>
            <span>GitHub</span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-end justify-between space-x-1">
        {visibleData.map((item) => {
          const heightPx = item.total > 0 ? Math.max(2, (item.total / maxValue) * 180) : 0;
          return (
            <div key={item.month} className="flex flex-col items-center space-y-2 flex-1 min-w-0" title={`${item.label}: AWS ${formatCost(item.aws)} | GCP ${formatCost(item.gcp)} | GitHub ${formatCost(item.github)}`}>
              <div className="text-xs text-gray-500 truncate">{formatCost(item.total)}</div>
              <div className="w-full flex flex-col-reverse" style={{ height: `${heightPx}px` }}>
                {item.aws > 0 && (
                  <div className="w-full bg-orange-500" style={{ height: `${(item.aws / item.total) * 100}%` }}></div>
                )}
                {item.gcp > 0 && (
                  <div className="w-full bg-blue-500" style={{ height: `${(item.gcp / item.total) * 100}%` }}></div>
                )}
                {item.github > 0 && (
                  <div className="w-full bg-gray-600" style={{ height: `${(item.github / item.total) * 100}%` }}></div>
                )}
              </div>
              <div className="text-xs text-gray-600">{item.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Service-wise Trends Chart
interface ServiceTrendItem {
  serviceName: string;
  totalCost: number;
  monthly: { month: string; label: string; cost: number }[];
}

const ServiceWiseTrendsChart: React.FC<{ data: CostData | null; dateRange?: string }> = ({ data, dateRange = '12m' }) => {
  const [services, setServices] = useState<ServiceTrendItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getTwelveMonthAnalysis()
      .then(res => {
        if (!cancelled) {
          setServices(res?.topServices || []);
          setError(null);
        }
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load service trends'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading service trends...</div>;
  }
  if (error) {
    return <div className="h-full flex items-center justify-center text-xs text-red-600">{error}</div>;
  }
  if (!services || services.length === 0) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">No service trend data available</div>;
  }

  // Slice each service's monthly array based on dateRange
  const monthsToShow = dateRange === '3m' ? 3 : dateRange === '6m' ? 6 : 12;
  const sliced = services.map(s => ({
    ...s,
    monthly: s.monthly.slice(-monthsToShow)
  }));

  const monthLabels = sliced[0]?.monthly.map(m => m.label) || [];
  const allCosts = sliced.flatMap(s => s.monthly.map(m => m.cost));
  const maxCost = Math.max(...allCosts, 1);

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  const bgColors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500'];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-gray-700">Service-wise Cost Trends</span>
      </div>
      <div className="flex-1 relative">
        <svg width="100%" height="200" viewBox="0 0 400 200" preserveAspectRatio="none">
          {sliced.map((svc, svcIdx) => {
            const color = colors[svcIdx % colors.length];
            const points = svc.monthly.map((m, idx) => {
              const x = svc.monthly.length === 1
                ? 200
                : (idx / (svc.monthly.length - 1)) * 350 + 25;
              const y = 180 - (m.cost / maxCost) * 160;
              return { x, y, cost: m.cost, label: m.label };
            });
            const pathData = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
            return (
              <g key={svc.serviceName}>
                <path d={pathData} stroke={color} strokeWidth="2" fill="none" />
                {points.map((p, idx) => (
                  <circle key={idx} cx={p.x} cy={p.y} r="3" fill={color}>
                    <title>{`${svc.serviceName} — ${p.label}: $${p.cost.toLocaleString()}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
        <div className="absolute bottom-0 left-0 right-0 flex justify-between px-6 text-xs text-gray-500">
          {monthLabels.map((label, idx) => <span key={`${label}-${idx}`}>{label}</span>)}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {sliced.map((svc, idx) => (
          <div key={svc.serviceName} className="flex items-center space-x-1">
            <div className={`w-2.5 h-2.5 rounded-full ${bgColors[idx % bgColors.length]}`}></div>
            <span className="text-gray-600" title={svc.serviceName}>
              {svc.serviceName.replace(/^Amazon\s+/i, '').replace(/^AWS\s+/i, '').slice(0, 18)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Tag-based Cost Distribution
interface TagItem {
  tag: string;
  cost: number;
  percentage: number;
}

const TagBasedCostChart: React.FC<{ data: CostData | null }> = ({ data }) => {
  const [tagData, setTagData] = useState<TagItem[] | null>(null);
  const [tagKey, setTagKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getTagBasedCosts()
      .then(res => {
        if (!cancelled) {
          setTagData(res?.tags || []);
          setTagKey(res?.tagKey || null);
          setError(null);
        }
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load tag costs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const formatCost = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    if (n >= 1) return `$${n.toFixed(2)}`;
    return n > 0 ? '<$1' : '$0';
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium text-gray-700">Tag-based Cost Distribution</span>
          <FontAwesomeIcon icon={faTags} className="text-purple-500" />
        </div>
        <div className="flex-1 flex items-center justify-center text-xs text-gray-500">Loading tag-based costs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium text-gray-700">Tag-based Cost Distribution</span>
          <FontAwesomeIcon icon={faTags} className="text-purple-500" />
        </div>
        <div className="flex-1 flex items-center justify-center text-xs text-red-600 px-4 text-center">{error}</div>
      </div>
    );
  }

  if (!tagData || tagData.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium text-gray-700">Tag-based Cost Distribution</span>
          <FontAwesomeIcon icon={faTags} className="text-purple-500" />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-xs text-gray-500 text-center px-4">
          <div className="font-medium mb-1">No cost allocation tags found</div>
          <div className="text-gray-400">Activate cost allocation tags in AWS Billing Console to see tag-wise breakdown</div>
        </div>
      </div>
    );
  }

  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500', 'bg-red-500', 'bg-pink-500', 'bg-indigo-500'];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-700">
          Tag-based Cost Distribution {tagKey && <span className="text-gray-400">({tagKey})</span>}
        </span>
        <FontAwesomeIcon icon={faTags} className="text-purple-500" />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto">
        {tagData.slice(0, 8).map((item, index) => (
          <div key={item.tag} className="flex items-center space-x-3">
            <div className="w-28 text-xs text-gray-600 truncate" title={item.tag}>{item.tag}</div>
            <div className="flex-1">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${colors[index % colors.length]}`}
                  style={{ width: `${Math.max(2, item.percentage)}%` }}
                ></div>
              </div>
            </div>
            <div className="w-16 text-xs text-gray-900 text-right">{formatCost(item.cost)}</div>
            <div className="w-10 text-xs text-gray-500 text-right">{item.percentage.toFixed(1)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// AWS Pricing Model Breakdown
interface PricingItem { model: string; cost: number; percentage: number; }

const AWSPricingModelChart: React.FC<{ data: CostData | null }> = ({ data }) => {
  const [pricingData, setPricingData] = useState<PricingItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getPricingAndUtilization()
      .then(res => {
        if (cancelled) return;
        const pb = res?.aws?.pricingBreakdown;
        if (pb) {
          setPricingData([
            { model: 'On-Demand', cost: pb.onDemand?.cost || 0, percentage: pb.onDemand?.percentage || 0 },
            { model: 'Reserved Instances', cost: pb.reserved?.cost || 0, percentage: pb.reserved?.percentage || 0 },
            { model: 'Savings Plans', cost: pb.savingsPlans?.cost || 0, percentage: pb.savingsPlans?.percentage || 0 },
            { model: 'Spot Instances', cost: pb.spot?.cost || 0, percentage: pb.spot?.percentage || 0 }
          ]);
        } else {
          setPricingData([]);
        }
        setError(null);
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load pricing data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const formatCost = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    if (n >= 1) return `$${n.toFixed(2)}`;
    return n > 0 ? '<$1' : '$0';
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading pricing model data...</div>;
  }
  if (error) {
    return <div className="h-full flex items-center justify-center text-xs text-red-600 px-4 text-center">{error}</div>;
  }
  if (!pricingData || pricingData.every(p => p.cost === 0)) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-xs text-gray-500 text-center px-4">
        <div className="font-medium mb-1">No EC2 compute spend found</div>
        <div className="text-gray-400">Pricing model breakdown applies to EC2 compute usage</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-700">AWS EC2 Pricing Model Breakdown</span>
        <FontAwesomeIcon icon={faCreditCard} className="text-orange-500" />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto">
        {pricingData.map((item) => (
          <div key={item.model} className="border rounded p-2">
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium text-xs text-gray-900">{item.model}</div>
              <div className="text-xs text-gray-900">{formatCost(item.cost)}</div>
            </div>
            <div className="text-xs text-gray-600 mb-1">{item.percentage}% of EC2 compute</div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="h-2 rounded-full bg-orange-500" style={{ width: `${Math.max(2, item.percentage)}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// GCP CUD/SUD Breakdown
const GCPCommitmentChart: React.FC<{ data: CostData | null }> = ({ data }) => {
  const [commitmentData, setCommitmentData] = useState([
    { type: 'On-Demand', cost: 0, percentage: 0, savings: 0 },
    { type: 'Committed Use Discounts', cost: 0, percentage: 0, savings: 0 },
    { type: 'Sustained Use Discounts', cost: 0, percentage: 0, savings: 0 }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getPricingAndUtilization()
      .then(res => {
        if (!cancelled && res?.gcp?.commitments) {
          const gc = res.gcp.commitments;
          setCommitmentData([
            { type: 'On-Demand', cost: gc.onDemand?.cost || 0, percentage: gc.onDemand?.percentage || 0, savings: 0 },
            { type: 'Committed Use Discounts', cost: gc.cud?.cost || 0, percentage: gc.cud?.percentage || 0, savings: gc.cud?.savings || 0 },
            { type: 'Sustained Use Discounts', cost: gc.sud?.cost || 0, percentage: gc.sud?.percentage || 0, savings: gc.sud?.savings || 0 }
          ]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const formatCost = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    if (n >= 1) return `$${n.toFixed(0)}`;
    return '$0';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-gray-500">
        Loading GCP commitment data...
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-700">GCP Commitment & Discounts</span>
        <FontAwesomeIcon icon={faPercent} className="text-blue-500" />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto pr-1">
        {commitmentData.map((item, index) => (
          <div key={item.type} className="border rounded-lg p-2">
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium text-xs text-gray-900">{item.type}</div>
              <div className="text-xs text-gray-900">{formatCost(item.cost)}</div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="text-gray-600">{item.percentage}% of GCP spend</div>
              {item.savings > 0 && (
                <div className="text-green-600">
                  {formatCost(item.savings)} saved
                </div>
              )}
            </div>
            <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-blue-500"
                style={{ width: `${item.percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface UtilItem { resource: string; utilization: number; coverage: number; }

const UtilizationCoverageChart: React.FC<{ data: CostData | null }> = ({ data }) => {
  const [utilData, setUtilData] = useState<UtilItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getPricingAndUtilization()
      .then(res => {
        if (cancelled) return;
        const items: UtilItem[] = [];
        if (res?.aws?.reservedInstances) {
          items.push({
            resource: 'AWS Reserved Instances',
            utilization: res.aws.reservedInstances.utilization || 0,
            coverage: res.aws.reservedInstances.coverage || 0
          });
        }
        if (res?.aws?.savingsPlans) {
          items.push({
            resource: 'AWS Savings Plans',
            utilization: res.aws.savingsPlans.utilization || 0,
            coverage: res.aws.savingsPlans.coverage || 0
          });
        }
        setUtilData(items);
        setError(null);
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load utilization data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading utilization data...</div>;
  }
  if (error) {
    return <div className="h-full flex items-center justify-center text-xs text-red-600 px-4 text-center">{error}</div>;
  }
  if (!utilData || utilData.length === 0 || utilData.every(u => u.utilization === 0 && u.coverage === 0)) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-xs text-gray-500 text-center px-4">
        <div className="font-medium mb-1">No Reserved Instances or Savings Plans</div>
        <div className="text-gray-400">Purchase RIs or SPs to see utilization & coverage</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-700">Utilization & Coverage Report</span>
        <FontAwesomeIcon icon={faGauge} className="text-green-500" />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto">
        {utilData.map((item) => (
          <div key={item.resource} className="border rounded p-2">
            <div className="font-medium text-xs text-gray-900 mb-2">{item.resource}</div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Utilization Rate</span>
                <span className="font-medium">{item.utilization}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-green-500" style={{ width: `${Math.max(1, item.utilization)}%` }}></div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Coverage Rate</span>
                <span className="font-medium">{item.coverage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${Math.max(1, item.coverage)}%` }}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PaymentRow { month: string; label: string; aws: number; gcp: number; github: number; total: number; }

const MonthlyPaymentChart: React.FC<{ data: CostData | null; dateRange?: string }> = ({ data, dateRange = '12m' }) => {
  const [paymentData, setPaymentData] = useState<PaymentRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    costService.getTwelveMonthAnalysis()
      .then(res => {
        if (!cancelled) {
          setPaymentData(res?.months || []);
          setError(null);
        }
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load monthly payments'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const formatCost = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    if (n >= 1) return `$${n.toFixed(0)}`;
    return n > 0 ? '<$1' : '$0';
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading monthly payment data...</div>;
  }
  if (error) {
    return <div className="h-full flex items-center justify-center text-xs text-red-600 px-4 text-center">{error}</div>;
  }
  if (!paymentData || paymentData.length === 0) {
    return <div className="h-full flex items-center justify-center text-xs text-gray-500">No payment data available</div>;
  }

  // Slice based on dateRange
  const monthsToShow = dateRange === '3m' ? 3 : dateRange === '6m' ? 6 : 12;
  const visible = paymentData.slice(-monthsToShow);
  const avgTotal = visible.length > 0 ? visible.reduce((s, p) => s + p.total, 0) / visible.length : 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-700">Monthly Payment Breakdown ({visible.length} months)</span>
        <FontAwesomeIcon icon={faMoneyBill} className="text-green-600" />
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-gray-600 border-b">
              <th className="text-left p-1">Month</th>
              <th className="text-right p-1">AWS</th>
              <th className="text-right p-1">GCP</th>
              <th className="text-right p-1">GitHub</th>
              <th className="text-right p-1">Total</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.month} className="border-t">
                <td className="p-1 font-medium">{p.label}</td>
                <td className="p-1 text-right text-orange-600">{formatCost(p.aws)}</td>
                <td className="p-1 text-right text-blue-600">{formatCost(p.gcp)}</td>
                <td className="p-1 text-right text-gray-600">{formatCost(p.github)}</td>
                <td className="p-1 text-right font-medium">{formatCost(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 p-2 bg-gray-50 rounded">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Average Monthly Bill</span>
          <span className="font-semibold text-gray-900">{formatCost(avgTotal)}</span>
        </div>
      </div>
    </div>
  );
};

export const VisualizationTab: React.FC<VisualizationTabProps> = ({
  data,
  loading = false,
  filters = {},
  onFiltersChange
}) => {
  const [dateRange, setDateRange] = useState('12m');
  const [showFilters, setShowFilters] = useState(false);

  // AI Analysis
  const { aiAnalysis, showAIInsights, analyzing, fetchAnalysis, setShowAIInsights } = useAIAnalysis('visualization');

  // Check if we have real data to display
  const hasRealData = data && data.summary && data.summary.totalSpend > 0;

  const handleAIAnalysis = async () => {
    await fetchAnalysis({
      data,
      dateRange,
      hasRealData,
      summary: data?.summary,
      clouds: data?.clouds
    });
  };

  return (
    <div className="space-y-3 font-mono">
      {/* Show message if no real data available */}
      {!hasRealData && !loading && (
        <div className="bg-blue-50 border border-blue-300 rounded p-3">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-blue-900 mb-1">No Cost Data Available</h3>
              <p className="text-xs text-blue-700">
                Connect your AWS, GCP, or GitHub accounts in Settings &gt; Connected Accounts to see cost visualizations and trends.
              </p>
            </div>
          </div>
        </div>
      )}

      {hasRealData && (
        <>
          {/* Show real data summary card */}
          <div className="bg-green-50 border border-green-300 rounded p-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Current Month Total Spend</h3>
                <p className="text-lg font-bold text-gray-900">${data.summary.totalSpend.toLocaleString()}</p>
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-gray-600">
                    AWS: ${data.clouds?.aws?.totalSpend?.toLocaleString() || '0'}  •
                    GCP: ${data.clouds?.gcp?.totalSpend?.toLocaleString() || '0'}  •
                    GitHub: ${data.clouds?.github?.totalSpend?.toLocaleString() || '0'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600 mb-1">Monthly Change</div>
                <div className={`text-sm font-bold ${data.summary.monthlyDelta >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {data.summary.monthlyDelta >= 0 ? '+' : ''}{data.summary.monthlyDelta > 0 ? '$' + data.summary.monthlyDelta.toLocaleString() : 'No change'}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header with Filters */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-gray-900 mb-1">Advanced Cost Visualizations</h2>
            <p className="text-xs text-gray-600">
              Comprehensive analytics including trends, utilization, pricing models, and payment breakdowns
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-xs" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 font-mono"
              >
                <option value="3m">Last 3 Months</option>
                <option value="6m">Last 6 Months</option>
                <option value="12m">Last 12 Months</option>
              </select>
            </div>
            <AIInsightsButton
              onClick={handleAIAnalysis}
              loading={analyzing}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded text-xs font-semibold hover:bg-gray-50 transition-colors"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-1" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* AI Insights Panel */}
      {showAIInsights && aiAnalysis && (
        <AIInsightsPanel
          analysis={aiAnalysis}
          onClose={() => setShowAIInsights(false)}
        />
      )}

      {/* Quick Filters */}
      {showFilters && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Cloud Providers</label>
              <div className="space-y-1">
                {['AWS', 'GCP', 'GitHub'].map(cloud => (
                  <label key={cloud} className="flex items-center text-xs">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    {cloud}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Service Types</label>
              <div className="space-y-1">
                {['Compute', 'Storage', 'Networking', 'Database'].map(service => (
                  <label key={service} className="flex items-center text-xs">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    {service}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Pricing Models</label>
              <div className="space-y-1">
                {['On-Demand', 'Reserved', 'Spot', 'Committed'].map(model => (
                  <label key={model} className="flex items-center text-xs">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    {model}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Regions</label>
              <div className="space-y-1">
                {['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'].map(region => (
                  <label key={region} className="flex items-center text-xs">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    {region}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Past Month Trends - Full Width (range controlled by header dropdown) */}
        <ChartCard
          title={`Past ${dateRange === '3m' ? '3' : dateRange === '6m' ? '6' : '12'} Months Cost Trends`}
          description="Month-over-month spending patterns across all cloud providers"
          icon={faChartLine}
          color="bg-blue-500"
          loading={loading}
          fullWidth={true}
        >
          <TwelveMonthTrendChart data={data} dateRange={dateRange} />
        </ChartCard>

        {/* Service-wise Trends */}
        <ChartCard
          title="Service-wise Trends"
          description="Individual service cost progression over time"
          icon={faServer}
          color="bg-green-500"
          loading={loading}
        >
          <ServiceWiseTrendsChart data={data} dateRange={dateRange} />
        </ChartCard>

        {/* Tag-based Distribution */}
        <ChartCard
          title="Tag-based Cost Distribution"
          description="Cost allocation by resource tags and teams"
          icon={faTags}
          color="bg-purple-500"
          loading={loading}
        >
          <TagBasedCostChart data={data} />
        </ChartCard>

        {/* AWS Pricing Models */}
        <ChartCard
          title="AWS Pricing Model Breakdown"
          description="Spot vs Reserved vs Savings Plans distribution"
          icon={faCreditCard}
          color="bg-orange-500"
          loading={loading}
        >
          <AWSPricingModelChart data={data} />
        </ChartCard>

        {/* GCP Commitments */}
        <ChartCard
          title="GCP Commitment Discounts"
          description="CUD and SUD utilization and savings"
          icon={faPercent}
          color="bg-blue-600"
          loading={loading}
        >
          <GCPCommitmentChart data={data} />
        </ChartCard>

        {/* Utilization & Coverage */}
        <ChartCard
          title="Utilization & Coverage Report"
          description="Reserved instance and commitment utilization rates"
          icon={faGauge}
          color="bg-green-600"
          loading={loading}
        >
          <UtilizationCoverageChart data={data} />
        </ChartCard>

        {/* Monthly Payments - Full Width */}
        <ChartCard
          title="Monthly Payment Breakdown"
          description="Detailed monthly billing across all providers and services"
          icon={faMoneyBill}
          color="bg-indigo-600"
          loading={loading}
          fullWidth={true}
        >
          <MonthlyPaymentChart data={data} dateRange={dateRange} />
        </ChartCard>
      </div>
    </div>
  );
};